import { useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { FormattedMessage } from 'react-intl'
import { NavLink } from 'react-router-dom'
import { isEmpty } from 'lodash'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { getDateRange } from 'features/App/components/Filters/DateRangeFilter'
import { filterEmptyValue } from 'utils/object'

const LIMIT = 30

const GET_INVENTORY_WASTES = gql`
  query GetInventoryWastes(
    $limit: Int!
    $page: Int!
    $inventoryId: ID
    $id: ID
    $itemId: ID
    $creationDateRange: dateRangeInput
  ) {
    inventoryWastes(
      pagination: { size: $limit, page: $page }
      input: { inventoryID: $inventoryId, id: $id, itemID: $itemId, creationDateRange: $creationDateRange }
    ) {
      total
      items {
        id
        createdAt
        status
        reason
        inventory {
          id
          name
        }
        user {
          id
          firstName
          lastName
        }
        items {
          id
          name
          quantity
          price
          subtotal
        }
        amount
      }
    }
  }
`

export const withInventoryWastes =
  (List: TSFixMe, Pagination: TSFixMe): TSFixMe =>
  // eslint-disable-next-line react/display-name
  ({ urls = {} as TSFixMe, filters = {} as TSFixMe, ...props }): TSFixMe => {
    const variables = filterEmptyValue({
      page: props.page - 1,
      limit: LIMIT,
      inventoryId: props.inventoryId || (filters.inventory && filters.inventory.id),
      id: filters.id,
      itemId: filters.itemId,
      creationDateRange: getDateRange(filters.creationDateRange)
    })

    const {
      loading,
      error,
      data = {},
      refetch,
      networkStatus
    } = useQuery(GET_INVENTORY_WASTES, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      variables
    })

    useEffect((): TSFixMe => {
      refetch()
    }, [])

    if (networkStatus === NetworkStatus.refetch || loading) {
      return (
        <EmptyState.Container>
          <Loader />
        </EmptyState.Container>
      )
    } else if (error) {
      return (
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="spanish.inventory.wastes.error.description"
              defaultMessage="Hubo un error al cargar la merma"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button onClick={(): TSFixMe => refetch()} kind="primary">
              <FormattedMessage id="spanish.inventory.wastes.error.action" defaultMessage="Recargar la merma" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      )
    } else if (
      isEmpty(data.inventoryWastes.items) &&
      props.page === 1 &&
      isEmpty(
        filterEmptyValue({
          id: filters.id,
          itemId: filters.itemId,
          creationDateRange: getDateRange(filters.creationDateRange)
        })
      )
    ) {
      return (
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="spanish.inventory.wastes.empty.description"
              defaultMessage="No tienes merma todavía"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button
              as={NavLink}
              to={urls.new ? urls.new : `/inventories/${props.inventoryId}/remove-bulk-items`}
              kind="primary"
            >
              <FormattedMessage id="spanish.inventory.wastes.empty.action" defaultMessage="Agregar merma" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      )
    } else if (isEmpty(data.inventoryWastes.items)) {
      return (
        <EmptyState.Container>
          <EmptyState.Description>
            <FormattedMessage
              id="spanish.inventory.wastes.empty.filters.description"
              defaultMessage="No se encontró ninguna merma"
            />
          </EmptyState.Description>

          <EmptyState.Action>
            <Button as={NavLink} to={urls.list ? urls.list : `/inventories/${props.inventoryId}/wastes`} kind="primary">
              <FormattedMessage id="spanish.inventory.wastes.empty.filters.action" defaultMessage="Borrar filtros" />
            </Button>
          </EmptyState.Action>
        </EmptyState.Container>
      )
    } else {
      return (
        <>
          <List wastes={data.inventoryWastes.items} {...props} />

          <Pagination
            totalItems={data.inventoryWastes.total}
            currentItemsCount={data.inventoryWastes.items.length}
            filters={filters}
            {...props}
          />
        </>
      )
    }
  }
